import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authService, type UserProfile, type LoginRequest } from '@/services/auth.service';
import apiService from '@/services/api.service';

interface MicrosoftLoginResult {
  created: boolean;
  needsProfileCompletion: boolean;
}

interface AuthContextData {
  user: UserProfile | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginRequest) => Promise<void>;
  loginWithMicrosoft: (idToken: string) => Promise<MicrosoftLoginResult>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  updateProfile: (data: Partial<Pick<UserProfile, 'name' | 'bio' | 'avatar'>>) => Promise<void>;
  completeTutorial: (key?: string) => Promise<void>;
  resetTutorial: (key?: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

const TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Configura funcao de obter token no apiService
  useEffect(() => {
    apiService.setAccessTokenGetter(async () => {
      const token = localStorage.getItem(TOKEN_KEY);
      return token;
    });
  }, []);

  // Carrega usuario ao montar
  useEffect(() => {
    loadUser();
  }, []);

  const loadUser = async () => {
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);

      if (!token || !refreshToken) {
        setIsLoading(false);
        return;
      }

      try {
        const userData = await authService.getMe();
        setUser(userData);
      } catch (error: unknown) {
        const axiosError = error as { response?: { status?: number } };
        if (axiosError?.response?.status === 401) {
          try {
            const { access_token } = await authService.refreshToken(refreshToken);
            localStorage.setItem(TOKEN_KEY, access_token);

            const userData = await authService.getMe();
            setUser(userData);
          } catch {
            localStorage.removeItem(TOKEN_KEY);
            localStorage.removeItem(REFRESH_TOKEN_KEY);
            setUser(null);
          }
        } else {
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem(REFRESH_TOKEN_KEY);
          setUser(null);
        }
      }
    } catch {
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (credentials: LoginRequest) => {
    const response = await authService.login(credentials);

    localStorage.setItem(TOKEN_KEY, response.access_token);
    localStorage.setItem(REFRESH_TOKEN_KEY, response.refresh_token);

    // Load full user profile (login response has partial data)
    const userData = await authService.getMe();
    setUser(userData);
  };

  const loginWithMicrosoft = async (idToken: string): Promise<MicrosoftLoginResult> => {
    const response = await authService.microsoftLogin(idToken);

    localStorage.setItem(TOKEN_KEY, response.access_token);
    localStorage.setItem(REFRESH_TOKEN_KEY, response.refresh_token);

    // Load full user profile
    const userData = await authService.getMe();
    setUser(userData);

    return {
      created: response.created,
      needsProfileCompletion: userData.needs_profile_completion,
    };
  };

  const logout = async () => {
    try {
      const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
      if (refreshToken) {
        await authService.logout(refreshToken);
      }
    } catch {
      // Ignore logout errors
    } finally {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      setUser(null);
    }
  };

  const refreshUser = async () => {
    try {
      const userData = await authService.getMe();
      setUser(userData);
    } catch {
      // Ignore refresh errors
    }
  };

  const updateProfile = async (data: Partial<Pick<UserProfile, 'name' | 'bio' | 'avatar'>>) => {
    const updatedUser = await authService.updateProfile(data);
    setUser(updatedUser);
  };

  const completeTutorial = async (key: string = 'main') => {
    const updatedUser = await authService.completeTutorial(key);
    setUser(updatedUser);
  };

  const resetTutorial = async (key: string = 'main') => {
    const updatedUser = await authService.resetTutorial(key);
    setUser(updatedUser);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        loginWithMicrosoft,
        logout,
        refreshUser,
        updateProfile,
        completeTutorial,
        resetTutorial,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuthContext() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext deve ser usado dentro de AuthProvider');
  }
  return context;
}
